import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { Grid, SelectChangeEvent, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import DvrIcon from "@mui/icons-material/Dvr";
import BackendPaginationTable from "common/Table/TableWithPagination";
import { ClientProfileService } from "subModule/src/core/services";
import { DeleteData } from "subModule/src/type";
import Loader from "layout/Loader";
import { useGetHostingDetailsService } from "subModule/src/services/client/useClient";
import { HostingDetails } from "subModule/src/services/client/interface";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import HostingDetailsSearchHeader from "./HostingDetailsSearchHeader";
interface SearchFormData {
  [key: string]: string | any;
}
export default function HostingDetailsListing() {
  const navigate = useNavigate();
  const loggedInUserData = useSelector(selectUserInfo);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["title", "userName"]);
  const [search, setSearch] = useState<any>({});
  const [row, setRow] = useState<HostingDetails>({});
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    title: "",
    userName: "",
  });
  const {
    data: tableData,
    refetch,
    isFetching,
  } = useGetHostingDetailsService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => navigate(`/hosting-details/` + row.id);
  const showRowView = (row: any) => {
    navigate(`/hosting-details/` + row.id);
  };
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setFilter(["title", "userName"]);
    setSearchFormData({ title: "", userName: "" });
    if (Object.keys(search).length) {
      setSearch({});
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = value;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = value?.trim();
      }
    });
    if (
      Object.keys(filteredSearch).length > 0 &&
      Object.values(filteredSearch).some((value) => value)
    ) {
      setSearch(filteredSearch);
      setPageNo(1);
      setPageSize(pageSize);
    }
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/hosting-details/create")}
        heading={t("hostingDetails")}
        icon={
          <DvrIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={loggedInUserData?.type === -1}
      />
      <Box>
        {isFetching || loading ? (
          <Loader />
        ) : (
          <BackendPaginationTable
            columns={[
              { id: "title", label: "title", numeric: false, width: "28%" },
              {
                id: "userNameHostingDetails",
                label: "userName",
                numeric: false,
                width: "24%",
              },
              { id: "url", label: "url", numeric: false, width: "32%" },
              { id: "actions", label: "actions", numeric: false, width: "10%" },
            ]}
            data={tableData?.pages[0]?.data?.content || []}
            pageNo={pageNo}
            pageSize={pageSize}
            tableData={tableData?.pages[0]?.data || {}}
            showCheckbox={false}
            showActions={true}
            enableSorting={true}
            showHeader={true}
            showSearch={true}
            showFilter={true}
            showCreateButton={true}
            actions={actionslist}
            handleChangePage={handleChangePage}
            handleChangeRows={handleChangeRows}
            rowClick={loggedInUserData?.type === -1}
            showRowView={showRowView}
            loadingSearch={false}
            headerComponent={
              <>
                <HostingDetailsSearchHeader
                  searchFormData={searchFormData}
                  handleChangeFilter={handleChangeFilter}
                  handleChangeSearch={handleChangeSearch}
                  handleSubmitSearch={handleSubmitSearch}
                  clearSearch={clearSearch}
                  {...{ filter, search }}
                />
              </>
            }
          />
        )}
      </Box>
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("hostingDetails")}
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            ClientProfileService.deleteHostingDetails((row as any).id)
              .then((response: DeleteData) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("hostingDetails") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(<AlertMessages text={response.message} />, {
                    variant: "error",
                  });
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
    </Box>
  );
}
